import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { CoordinateHelper, MapClickToolConfigModel, MapClickToolModel, MapService, ToolTypeEnum } from '@tailormap-viewer/map';
import { selectComponentsConfigForType, selectComponentTitle } from '../../../state/core.selectors';
import { BaseComponentTypeEnum, CoordinateLinkWindowConfigModel, CoordinateLinkWindowConfigUrlModel } from '@tailormap-viewer/api';
import { combineLatest, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { take, withLatestFrom } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ComponentRegistrationService } from '../../../services';
import { CoordinateLinkWindowMenuButtonComponent } from './coordinate-link-window-menu-button/coordinate-link-window-menu-button.component';
import { MenubarService } from '../../menubar';
import { MobileLayoutService } from '../../../services/viewer-layout/mobile-layout.service';
import { SnackBarMessageComponent, SnackBarMessageOptionsModel, TooltipDirective, AutoFocusDirective } from '@tailormap-viewer/shared';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSelectionList, MatListOption, MatListItemIcon, MatListItemTitle } from '@angular/material/list';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-coordinate-link-window',
    templateUrl: './coordinate-link-window.component.html',
    styleUrls: ['./coordinate-link-window.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButton,
        TooltipDirective,
        MatIcon,
        MatFormField,
        MatLabel,
        MatSelect,
        AutoFocusDirective,
        ReactiveFormsModule,
        MatOption,
        MatSelectionList,
        MatListOption,
        MatListItemIcon,
        MatListItemTitle,
        AsyncPipe,
    ],
})
export class CoordinateLinkWindowComponent implements OnInit, OnDestroy {
  private store$ = inject(Store);
  private mapService = inject(MapService);
  private destroyRef = inject(DestroyRef);
  private componentRegistrationService = inject(ComponentRegistrationService);
  private menubarService = inject(MenubarService);
  private mobileLayoutService = inject(MobileLayoutService);
  private snackBar = inject(MatSnackBar);


  public noExpansionPanel = input<boolean>(false);

  public toolActive = toSignal(this.mapService.someToolsEnabled$([BaseComponentTypeEnum.COORDINATE_LINK_WINDOW]));
  public urls$: Observable<CoordinateLinkWindowConfigUrlModel[]>;
  public title$: Observable<string>;
  private tool: string | undefined;
  public visible$ = combineLatest([
    this.menubarService.isComponentVisible$(BaseComponentTypeEnum.COORDINATE_LINK_WINDOW),
    this.mobileLayoutService.isMobileLayoutEnabled$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    map(([ visible, mobileLayoutEnabled ]) => visible || !mobileLayoutEnabled),
  );

  public urlControl = new FormControl<CoordinateLinkWindowConfigUrlModel | null>(null);

  constructor() {
    const config$ = this.store$.select(selectComponentsConfigForType<CoordinateLinkWindowConfigModel>(BaseComponentTypeEnum.COORDINATE_LINK_WINDOW))
      .pipe(map(config => config?.config));
    this.urls$ = config$.pipe(map(conf => conf?.urls || []));
    this.title$ = config$.pipe(map(conf => conf?.title || $localize `:@@core.coordinate-link-window.title:Coordinate Link Window`));
  }

  public ngOnInit(): void {
    this.urls$
      .pipe(
        filter(urls => !!urls && urls.length > 0),
        take(1),
      )
      .subscribe(urls => {
        if (urls && urls.length > 0) {
          this.urlControl.patchValue(urls[0]);
          this.createMapClickTool();
        }
      });

    this.componentRegistrationService.registerComponent(
      'mobile-menu-home',
      { type: BaseComponentTypeEnum.COORDINATE_LINK_WINDOW, component: CoordinateLinkWindowMenuButtonComponent },
    );

    // Toggle the CLW map tool when the CLW menu button is clicked in the mobile layout.
    this.mobileLayoutService.isMobileLayoutEnabled$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(enabled => enabled),
        switchMap(() => this.menubarService.isComponentVisible$(BaseComponentTypeEnum.COORDINATE_LINK_WINDOW)),
      ).subscribe(visibleInMobileLayout => {
        if (visibleInMobileLayout) {
          this.menubarService.setMobilePanelHeight(230);
          this.toggle(false);
        } else if (this.toolActive()) {
          this.toggle(true);
        }
      });

    // Close the CLW when the mapTool is disabled by another component.
    this.mapService.someToolsEnabled$([BaseComponentTypeEnum.COORDINATE_LINK_WINDOW])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        withLatestFrom(
          this.menubarService.isComponentVisible$(BaseComponentTypeEnum.COORDINATE_LINK_WINDOW),
          this.store$.select(selectComponentTitle(BaseComponentTypeEnum.MOBILE_MENUBAR_HOME, $localize `:@@core.home.menu:Menu`)),
          ),
      )
      .subscribe(([ enabledTool, visible, componentTitle ]) => {
        if (!enabledTool && visible) {
          this.menubarService.toggleActiveComponent(BaseComponentTypeEnum.MOBILE_MENUBAR_HOME, componentTitle);
        }
      });
  }

  public toggle(close?: boolean) {
    if (close === true || this.toolActive()) {
      this.mapService.disableTool(this.tool);
      return;
    }
    this.mapService.enableTool(this.tool, true);
  }

  private createMapClickTool() {
    this.mapService.createTool$<MapClickToolModel, MapClickToolConfigModel>({
      type: ToolTypeEnum.MapClick,
      owner: BaseComponentTypeEnum.COORDINATE_LINK_WINDOW,
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ tool }) => {
          this.tool = tool.id;
        }),
        switchMap(({ tool }) => tool.mapClick$),
        switchMap(({ mapCoordinates }) => {
          const currentUrl = this.urlControl.value;
          if (!currentUrl || !currentUrl.projection) {
            return of(mapCoordinates);
          }
          return this.mapService.getProjectionCode$()
            .pipe(take(1), map(mapProjection => {
              return CoordinateHelper.projectCoordinates(mapCoordinates, mapProjection, currentUrl.projection);
            }));
        }),
      ).subscribe(coordinates => {
        const currentUrl = this.urlControl.value;
        if (!currentUrl || !currentUrl.url || !coordinates || coordinates.length < 2) {
          return;
        }
        const urlWithCoordinates = currentUrl.url
          .replace(/\[(x|lon)]/i, "" + coordinates[0])
          .replace(/\[(y|lat)]/i, "" + coordinates[1]);
        if (/\[GPS-(X|Y|lat|lon)]/i.test(urlWithCoordinates)) {
          this.replaceGeoLocationPlaceholders(urlWithCoordinates, currentUrl.projection)
            .then(finalUrl => window.open(finalUrl, '_blank', 'popup=1, noopener, noreferrer'))
            .catch((err: string) => this.showSnackbarMessage(
              $localize `:@@core.coordinate-link-window.no-location:Failed to determine your location: ${err}.
              Your location is needed for this link.`,
            ));
        } else {
          window.open(urlWithCoordinates, '_blank', 'popup=1, noopener, noreferrer');
        }
      },
    );
  }

  public ngOnDestroy() {
    this.componentRegistrationService.deregisterComponent('mobile-menu-home', BaseComponentTypeEnum.COORDINATE_LINK_WINDOW);
  }

  private replaceGeoLocationPlaceholders(url: string, projection: string): Promise<string> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const [ x, y ] = CoordinateHelper.projectCoordinates(
            [ pos.coords.longitude, pos.coords.latitude ],
            'EPSG:4326',
            projection,
          );
          const result = url
            .replace(/\[GPS-X]/i, `${x}`)
            .replace(/\[GPS-Y]/i, `${y}`)
            .replace(/\[GPS-lat]/i, `${pos.coords.latitude}`)
            .replace(/\[GPS-lon]/i, `${pos.coords.longitude}`);
          resolve(result);
        },
        (err) => {
          reject(err.message);
        },
        { enableHighAccuracy: true },
      );
    });
  }

  private showSnackbarMessage(msg: string) {
    const config: SnackBarMessageOptionsModel = {
      message: msg,
      duration: 5000,
      showDuration: true,
      showCloseButton: true,
    };
    SnackBarMessageComponent.open$(this.snackBar, config).subscribe();
  }


}
